import { Injectable, inject } from '@angular/core';
import { MultiSectionFormPreviewApiService } from './multi-section-form-preview-api.service';
import {
  IConditionalLogic,
  INavigation,
  IQuesField,
} from './multi-section-form-preview.models';
import { MultiSectionFormPreviewService } from './multi-section-form-preview.service';
import { FieldInputHtmlResponseMapperService } from './field-input-html-response-mapper.service';
import { isArrayOfEmptyStrings } from './array.methods';
import { PreviewQuestionAnswerDetailsSaved } from '../buyer-preview.models';

/**
 * SM-3610: Interface for data-attribute-based conditional effects.
 * Defines effects that can be applied when a source field's answer triggers visibility.
 */
interface IConditionalEffect {
  targetId: string;
  value?: string;
  disabled?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class HandleConditionalLogicService {
  public msfapi = inject(MultiSectionFormPreviewApiService);
  public msfps = inject(MultiSectionFormPreviewService);
  private fihrm = inject(FieldInputHtmlResponseMapperService);

  multiSectionFormConditionalLogicJSON: IConditionalLogic[] = [];
  showFieldOnNavListItems(field: IQuesField) {
    
    if (
      Boolean(Number(this.multiSectionFormConditionalLogicJSON?.length) > 1)
    ) {
      if (this.msfps.getSection(field.sectionId)?.Title === 'Submitter') {
        return true;
      } else {
        return Boolean(
          this.getNodeLogicFor(field)?.mandatory &&
            !this.isFieldOptional(field) &&
            !this.msfps.doesHiddenInputExistFor(field),
        );
      }
    }
    return true;
  }

  showFieldOnPreviewArea(field: IQuesField) {
    if (
      Boolean(Number(this.multiSectionFormConditionalLogicJSON?.length) > 1)
    ) {
      if (this.msfps.getSection(field.sectionId)?.Title === 'Submitter') {
        return true;
      } else {
        return Boolean(
          this.getNodeLogicFor(field)?.mandatory ||
            this.isFieldOptional(field) ||
            this.msfps.doesHiddenInputExistFor(field),
        );
      }
    }
    return true;
  }

  isFieldOptional(field: IQuesField) {
    //  Condition 1: Field is optional when mandatory is not checked.
    // [Check if node has mandatory = false]
    const isFieldNotMandatory = !this.getNodeLogicFor(field)?.mandatory;

    //  Condition 2: Field is optional if it is not rendered conditionally
    // [Check if node is not part of any other node's mandatoryNodes[]
    const isFieldNotDependent = !this.isFieldShownConditionally(field);

   return Boolean(isFieldNotMandatory && isFieldNotDependent);
  }

  isFieldMandatoryNodeOfConditionItem(
    field: IQuesField,
    conditionItem: INavigation,
  ) {
    return (
      conditionItem?.mandatoryNodes &&
      conditionItem?.mandatoryNodes?.length !== 0 &&
      conditionItem?.mandatoryNodes?.includes(field.data[0].QuestionId)
    );
  }

  isFieldMandatoryNodeOfAnyConditionItemWithinLogicObj(
    field: IQuesField,
    logicObj: IConditionalLogic,
  ) {
    return (
      logicObj?.navigate &&
      logicObj?.navigate?.length !== 0 &&
      logicObj?.navigate?.some((conditionItem) => {
        return this.isFieldMandatoryNodeOfConditionItem(field, conditionItem);
      })
    );
  }

  isFieldShownConditionally(field: IQuesField) {
    const isDependent =
      this.multiSectionFormConditionalLogicJSON.filter(
        (logicObj: IConditionalLogic) => {
          return this.isFieldMandatoryNodeOfAnyConditionItemWithinLogicObj(
            field,
            logicObj,
          );
        },
      ) ?? [];
    return Boolean(isDependent.length);
  }

  toggleConditionalNodesFor(
    field: IQuesField,
    triggerSubjectAction: 'setAction' | 'clickAction' | null = null,
  ) {
    const fieldAnswerObjs = field?.nodeLogic?.selectedAnswers;
    // selectedAnswers may be plain strings ("Yes") OR objects ({ answer: "Yes" }),
    // depending on the code path that set them. Normalise both — otherwise string
    // answers map to `undefined`, the active/inactive split is wrong, and switching
    // to a non-trigger answer (e.g. Yes → No) fails to re-hide the revealed field.
    const fieldAnswerVals = fieldAnswerObjs?.map((a: any) =>
      typeof a === 'string' ? a : a?.answer,
    );
    const fieldNavigations = field?.nodeLogic?.navigate;
    const activeCondNavs = fieldNavigations?.filter((ansCondNav) =>
      fieldAnswerVals?.includes(ansCondNav?.condition),
    );
    const inactiveCondNavs = fieldNavigations?.filter(
      (ansCondNav) => !fieldAnswerVals?.includes(ansCondNav?.condition),
    );
    activeCondNavs?.forEach((ansCondNav) =>
      this.makeNodesVisibleInListItemsUI(ansCondNav?.mandatoryNodes, field),
    );

    // SM-3610: Apply data attribute effects from the source field
    this.applyDataAttributeEffects(field);

    // In the absence of the below "if" condition check - memory overflow issue happens
    if (
      triggerSubjectAction !== null &&
      triggerSubjectAction === 'clickAction'
    ) {
      inactiveCondNavs?.forEach((ansCondNav) => {
        this.handleSubNodes(ansCondNav?.mandatoryNodes);
      });
      this.showRespectiveMandatoryNodesForAllTheQuestionsAnswered();
    }
  }

  showRespectiveMandatoryNodesForAllTheQuestionsAnswered() {
    // Display mandatory nodes for only the selected condition For all the questions with answers selected
    this.msfps.allFields.forEach((qn: IQuesField) => {
      if (qn.answered) {
        if (
          qn.nodeLogic &&
          qn.nodeLogic.navigate &&
          qn.nodeLogic.navigate.length !== 0
        ) {
          const navigationForTheSelectedAnswers = qn.nodeLogic.navigate?.filter(
            (navigationItem) =>
              qn.nodeLogic.selectedAnswers
                .map((answerData) =>
                  typeof answerData === 'string'
                    ? answerData
                    : answerData['answer'].toLowerCase(),
                )
                .includes(navigationItem.condition.toLowerCase()),
          );
          navigationForTheSelectedAnswers?.forEach(
            (navigationForTheSelectedAnswer) => {
              if (
                navigationForTheSelectedAnswer &&
                navigationForTheSelectedAnswer.mandatoryNodes
              ) {
                this.makeNodesVisibleInListItemsUI(
                  navigationForTheSelectedAnswer.mandatoryNodes,
                );
              }
            },
          );
        }
      }
    });
  }

  handleSubNodes(fieldIds: string[]) {
    const fields = this.msfps.allFields?.filter((field) =>
      fieldIds.includes(field?.data[0]?.QuestionId),
    );
    const fieldsToBeHandled = fields;

    fieldsToBeHandled?.forEach((fieldToBeHandled) => {
      const fieldNavigationsToBeHandled =
        this.getNodeLogicFor(fieldToBeHandled)?.navigate;
      if (fieldNavigationsToBeHandled?.length) {
        fieldNavigationsToBeHandled.forEach((fieldNavigationToBeHandled) => {
          this.handleSubNodes(fieldNavigationToBeHandled?.mandatoryNodes);
        });
      } else {
        this.hideAllTheNodesIrrespectiveOfTheAnswerChoice(fieldToBeHandled);
      }
    });

    this.makeNodesHiddenInListItemsUIAndClearAnswers(fieldIds);
  }

  hideAllTheNodesIrrespectiveOfTheAnswerChoice(field: IQuesField) {
    const fieldNavigations = this.getNodeLogicFor(field)?.navigate;
    if (fieldNavigations) {
      fieldNavigations?.forEach((condNav) => {
        if (
          condNav?.mandatoryNodes?.length &&
          !isArrayOfEmptyStrings(condNav?.mandatoryNodes)
        ) {
          this.makeNodesHiddenInListItemsUIAndClearAnswers(
            condNav?.mandatoryNodes,
          );
        }
      });
    }
  }

  makeNodesVisibleInListItemsUI(nodes: string[], sourceField?: IQuesField) {
    nodes?.forEach((nodeId) => {
      const fieldToBeModifiedIdx = this.msfps.allFields?.findIndex(
        (field) => field?.data[0]?.QuestionId === nodeId,
      );
      if (this.msfps.allFields[fieldToBeModifiedIdx]) {
        this.msfps.allFields[fieldToBeModifiedIdx][
          'isQnFieldVisibleOnPreviewArea'
        ] = true;
        this.msfps.allFields[fieldToBeModifiedIdx][
          'isQnFieldVisibleOnNavListItems'
        ] = true;
      }
    });
  }

  /**
   * SM-3610: Apply data attribute effects from a source field.
   * Reads data-set-target, data-set-value, data-set-disabled from the selected option.
   */
  private applyDataAttributeEffects(sourceField: IQuesField): void {
    if (!sourceField || !sourceField.answered) return;

    const questionHtml = sourceField.data[1]?.question?.changingThisBreaksApplicationSecurity;
    if (!questionHtml) return;

    const fieldType = sourceField.data[1]?.type?.toLowerCase();
    if (!['radio', 'dropdown'].includes(fieldType)) return;

    // Get selected option ID from localStorage or nodeLogic
    const questionId = sourceField.data[0]?.QuestionId?.replace('Radio_', '').replace('Dropdown_', '');
    const localStorageData = questionId ? localStorage.getItem(questionId) : null;
    let selectedOptionId: string | null = null;

    if (localStorageData) {
      try {
        const parsed = JSON.parse(localStorageData);
        selectedOptionId = parsed.answerUniqueId?.[0] ?? null;
      } catch (e) {
        // Fall back to nodeLogic
      }
    }

    const selectedAnswers = sourceField.nodeLogic?.selectedAnswers;
    if (!selectedOptionId && selectedAnswers && selectedAnswers.length > 0) {
      const selectedAnswer = selectedAnswers[0];
      selectedOptionId = typeof selectedAnswer === 'object' ? selectedAnswer.optionid : null;
    }

    if (!selectedOptionId) return;

    // Parse HTML to find data attributes on the selected option
    const parser = new DOMParser();
    const doc = parser.parseFromString(questionHtml, 'text/html');
    let effects: IConditionalEffect[] = [];

    if (fieldType === 'radio') {
      const radioButtons = doc.querySelectorAll('input[type="radio"]');
      radioButtons.forEach((radio: Element) => {
        const radioInput = radio as HTMLInputElement;
        if (radioInput.value === selectedOptionId) {
          effects = this.extractDataAttributes(radioInput);
        }
      });
    } else if (fieldType === 'dropdown') {
      const options = doc.querySelectorAll('option');
      options.forEach((option: Element) => {
        const optionEl = option as HTMLOptionElement;
        if (optionEl.value === selectedOptionId) {
          effects = this.extractDataAttributesFromOption(optionEl);
        }
      });
    }

    // Apply effects to target fields
    effects.forEach(effect => this.applyEffectToTarget(effect));
  }

  /**
   * SM-3610: Extract data attributes from a radio input element.
   */
  private extractDataAttributes(element: Element): IConditionalEffect[] {
    const effects: IConditionalEffect[] = [];

    // Primary target
    const setTarget = element.getAttribute('data-set-target');
    if (setTarget) {
      effects.push({
        targetId: setTarget,
        value: element.getAttribute('data-set-value') ?? undefined,
        disabled: element.getAttribute('data-set-disabled') === 'true'
      });
    }

    // Numbered targets (data-set-target1, data-set-target2, etc.)
    let index = 1;
    let numberedTarget = element.getAttribute(`data-set-target${index}`);
    while (numberedTarget) {
      effects.push({
        targetId: numberedTarget,
        value: element.getAttribute(`data-set-value${index}`) ?? undefined,
        disabled: element.getAttribute(`data-set-disabled${index}`) === 'true'
      });
      index++;
      numberedTarget = element.getAttribute(`data-set-target${index}`);
    }

    return effects;
  }

  /**
   * SM-3610: Extract data attributes from a dropdown option element.
   */
  private extractDataAttributesFromOption(option: HTMLOptionElement): IConditionalEffect[] {
    const effects: IConditionalEffect[] = [];

    // Primary target
    const setTarget = option.getAttribute('data-set-target');
    if (setTarget) {
      effects.push({
        targetId: setTarget,
        value: option.getAttribute('data-set-value') ?? undefined,
        disabled: option.getAttribute('data-set-disabled') === 'true'
      });
    }

    // Numbered targets
    let index = 1;
    let numberedTarget = option.getAttribute(`data-set-target${index}`);
    while (numberedTarget) {
      effects.push({
        targetId: numberedTarget,
        value: option.getAttribute(`data-set-value${index}`) ?? undefined,
        disabled: option.getAttribute(`data-set-disabled${index}`) === 'true'
      });
      index++;
      numberedTarget = option.getAttribute(`data-set-target${index}`);
    }

    return effects;
  }

  /**
   * SM-3610: Apply an effect to a target field (set value and optionally lock).
   */
  private applyEffectToTarget(effect: IConditionalEffect): void {
    // Find target field by ID
    const questionId = effect.targetId.replace('Dropdown_', '').replace('Radio_', '').replace('Checkbox_', '');
    const targetFieldIndex = this.msfps.allFields?.findIndex(
      (f) => f.data[0]?.QuestionId === questionId
    ) ?? -1;

    if (targetFieldIndex === -1) return;

    const targetField = this.msfps.allFields[targetFieldIndex];
    if (!targetField) return;

    // Find the answer text that matches the value (optionid)
    const fieldType = targetField.data[1]?.type?.toLowerCase();
    let targetOption: any = null;
    let optionDataArray: any[] = [];

    if (fieldType === 'dropdown' && targetField.data[1]?.dropdowndata) {
      optionDataArray = targetField.data[1].dropdowndata;
      targetOption = optionDataArray.find((opt: any) => opt.optionid === effect.value);
    } else if (fieldType === 'radio' && targetField.data[1]?.radiodata) {
      optionDataArray = targetField.data[1].radiodata;
      targetOption = optionDataArray.find((opt: any) => opt.optionid === effect.value);
    }

    if (!targetOption) return;

    // Set target answer as selected, deselect others
    optionDataArray.forEach((opt) => {
      opt.selected = opt.optionid === targetOption.optionid;
    });

    // Update field state
    this.msfps.allFields[targetFieldIndex].answered = true;
    this.msfps.allFields[targetFieldIndex].valid = true;
    this.msfps.allFields[targetFieldIndex].isAnswerModified = true;
    this.msfps.allFields[targetFieldIndex].isQnFieldVisibleOnPreviewArea = true;
    this.msfps.allFields[targetFieldIndex].isQnFieldVisibleOnNavListItems = true;

    if (this.msfps.allFields[targetFieldIndex].nodeLogic) {
      this.msfps.allFields[targetFieldIndex].nodeLogic.selectedAnswers = [targetOption];
    } else {
      this.msfps.allFields[targetFieldIndex].nodeLogic = { selectedAnswers: [targetOption] } as any;
    }

    // Update localStorage
    const targetQuestionId = targetField.data[0].QuestionId.replace('Radio_', '').replace('Dropdown_', '');
    const fieldAnswer: PreviewQuestionAnswerDetailsSaved = {
      modifiedField: null,
      answerUniqueId: [targetOption.optionid],
      answerValue: [targetOption.answer],
      isAnswerValid: true,
      isAnswerModified: true,
    };
    localStorage.setItem(targetQuestionId, JSON.stringify(fieldAnswer));

    // Update the HTML UI
    let updatedHtml = this.fihrm.mapResponseIntoFieldUI(this.msfps.allFields[targetFieldIndex], 'setAction');
    if (updatedHtml) {
      // Optionally lock the field
      if (effect.disabled) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(updatedHtml, 'text/html');
        
        doc.querySelectorAll('input[type="radio"]').forEach((input: Element) => {
          (input as HTMLInputElement).setAttribute('disabled', 'true');
          (input as HTMLInputElement).style.backgroundColor = '#e0e0e0';
        });
        doc.querySelectorAll('select').forEach((select: Element) => {
          (select as HTMLSelectElement).setAttribute('disabled', 'true');
          (select as HTMLSelectElement).style.backgroundColor = '#e0e0e0';
        });
        doc.querySelectorAll('input[type="checkbox"]').forEach((input: Element) => {
          (input as HTMLInputElement).setAttribute('disabled', 'true');
        });

        const serializer = new XMLSerializer();
        updatedHtml = serializer.serializeToString(doc.body);
        updatedHtml = updatedHtml.replace(/^<body[^>]*>/, '').replace(/<\/body>$/, '');
      }
      
      this.msfps.allFields[targetFieldIndex].data[1].question.changingThisBreaksApplicationSecurity = updatedHtml;
    }

    // Update fieldsSegregatedIntoSections reference
    const updatedField = this.msfps.allFields[targetFieldIndex];
    if (this.msfps.fieldsSegregatedIntoSections?.[updatedField.sectionId]) {
      const fieldIdx = this.msfps.fieldsSegregatedIntoSections[updatedField.sectionId].fieldsList.findIndex(
        (f) => f.data[0].QuestionId === updatedField.data[0].QuestionId
      );
      if (fieldIdx !== -1) {
        this.msfps.fieldsSegregatedIntoSections[updatedField.sectionId].fieldsList[fieldIdx] = updatedField;
      }
    }
  }

  makeNodesHiddenInListItemsUI(nodes: string[]) {
    nodes?.forEach((nodeId) => {
      const fieldToBeModifiedIdx = this.msfps.allFields?.findIndex(
        (field) => field?.data[0]?.QuestionId === nodeId,
      );
      if (this.msfps.allFields[fieldToBeModifiedIdx]) {
        this.msfps.allFields[fieldToBeModifiedIdx][
          'isQnFieldVisibleOnPreviewArea'
        ] = false;
        this.msfps.allFields[fieldToBeModifiedIdx][
          'isQnFieldVisibleOnNavListItems'
        ] = false;
      }
    });
  }

  makeNodesHiddenInListItemsUIAndClearAnswers(nodes: string[]) {
    nodes?.forEach((nodeId) => {
      const fieldToBeModifiedIdx = this.msfps.allFields?.findIndex(
        (field) => field?.data[0]?.QuestionId === nodeId,
      );
      if (this.msfps.allFields[fieldToBeModifiedIdx]) {
        this.msfps.allFields[fieldToBeModifiedIdx][
          'isQnFieldVisibleOnPreviewArea'
        ] = false;
        this.msfps.allFields[fieldToBeModifiedIdx][
          'isQnFieldVisibleOnNavListItems'
        ] = false;
      }
      this.msfps.clearAnswerForField$ =
        this.msfps.allFields[fieldToBeModifiedIdx];
    });
  }

  getNodeLogicFor(field: IQuesField) {
    return this.multiSectionFormConditionalLogicJSON
      .map((item) => ({
        ...item,
        selectedAnswers: this.msfps.doesDisableInputExistFor(field)
          ? this.msfps.getDisabledFieldAnswer(field)
          : [],
      }))
      .find((item) => item.id === field.data[0].QuestionId);
  }

  getConditionalLogicWithNames(originalCqJSON: IConditionalLogic[]) {
    return [...originalCqJSON].map((logicObj: IConditionalLogic) => {
      let modifiedLogicObj = { ...logicObj };
      modifiedLogicObj.id = this.getQnName(logicObj.id);


      if (logicObj?.next && logicObj?.next?.length !== 0) {
        modifiedLogicObj.next = { ...logicObj }?.next?.map((nextNodeId) =>
          this.getQnName(nextNodeId),
        );
      }

      if (logicObj?.navigate && logicObj?.navigate?.length !== 0) {
        modifiedLogicObj.navigate = logicObj?.navigate?.map((conditionItem) => {
          let modifiedConditionItem = { ...conditionItem };

          if (conditionItem?.next && conditionItem?.next?.length !== 0) {
            modifiedConditionItem.next = { ...conditionItem }?.next?.map(
              (nextNodeId) => this.getQnName(nextNodeId),
            );
          }

          if (
            conditionItem?.mandatoryNodes &&
            conditionItem?.mandatoryNodes?.length !== 0
          ) {
            modifiedConditionItem.mandatoryNodes = {
              ...conditionItem,
            }?.mandatoryNodes?.map((mandatoryNode) =>
              this.getQnName(mandatoryNode),
            );
          }

          return modifiedConditionItem;
        });
      }

      return modifiedLogicObj;
    });
  }

  getQnName(qnId: string) {
    const name =
      qnId === ''
        ? qnId
        : this.msfps.allFields?.find(
            (qnObj) => qnObj?.data[0].QuestionId === qnId,
          )?.data[0].dispalyFCText;
    return name;
  }
}
